/**
 * Where a dependency arrow goes, and which way its head points.
 *
 * Two rules, and both of them were broken in a way that only shows on a real plan.
 *
 * **The head points the way the line arrives.** A finish-to-start link lands on the
 * left edge of the successor, so it arrives travelling right and the head points right.
 * A finish-to-finish link lands on the *right* edge, arrives travelling left, and its
 * head points left — that one is correct, not a bug. What is a bug is a head that
 * points left on a finish-to-start link, which is what you get from a naive three
 * segment elbow the moment the successor starts close behind the predecessor's end:
 * the last run reverses, and SVG's `orient="auto"` faithfully turns the head around.
 *
 * **The line stops outside the bar.** The arrowhead is drawn at the end of the path, so
 * a path that ends on the bar's edge puts the head on top of the bar. It stops a few
 * pixels short instead, and the head fills the gap.
 *
 * Pure geometry — no DOM, no React — because "the arrow points the wrong way" is
 * exactly the sort of thing that is obvious in a picture and invisible in a diff.
 */

/** Which ends of two bars a relation joins. */
export function anchors(relation: string): { from: 'start' | 'end'; to: 'start' | 'end' } {
  if (relation === 'SS') return { from: 'start', to: 'start' }
  if (relation === 'FF') return { from: 'end', to: 'end' }
  if (relation === 'SF') return { from: 'start', to: 'end' }
  return { from: 'end', to: 'start' }
}

/** How far a line runs before it turns. */
export const STUB = 9
/** How far short of the bar the line stops, leaving room for the arrowhead. */
export const GAP = 5
/** The shortest final run that still orients an arrowhead reliably. */
const MIN_RUN = 3

export type Side = 'start' | 'end'

/**
 * The elbow path from one bar's anchor to another's.
 *
 * `fx`/`tx` are the bar edges the link joins — the left edge for a start anchor, the
 * right edge for an end anchor. `rowHeight` is only used to find the lane between two
 * rows when the line has to double back.
 */
export function route(
  fx: number,
  fy: number,
  tx: number,
  ty: number,
  fromSide: Side,
  toSide: Side,
  rowHeight: number,
): string {
  // Which way the line leaves, and which way it must be travelling when it arrives.
  const out = fromSide === 'end' ? 1 : -1
  const inward = toSide === 'start' ? 1 : -1

  const exit = fx + out * STUB
  const tip = tx - inward * GAP
  const corner = tip - inward * STUB

  // Straight down and in, when the target is far enough ahead to arrive facing right.
  const roomToArrive = inward > 0 ? tip - exit >= MIN_RUN : exit - tip >= MIN_RUN
  if (roomToArrive) {
    return `M ${r(fx)} ${r(fy)} H ${r(exit)} V ${r(ty)} H ${r(tip)}`
  }

  // Otherwise double back through the lane between the two rows, so the final run is
  // still in the right direction. This is the case the old elbow got wrong: it simply
  // ran the last segment backwards and let the arrowhead flip.
  const lane = fy + (ty >= fy ? rowHeight / 2 : -rowHeight / 2)
  return (
    `M ${r(fx)} ${r(fy)} H ${r(exit)} V ${r(lane)} ` +
    `H ${r(corner)} V ${r(ty)} H ${r(tip)}`
  )
}

/** The direction the final run travels: 1 rightwards, -1 leftwards. */
export function headDirection(toSide: Side): 1 | -1 {
  return toSide === 'start' ? 1 : -1
}

/** Rounded, so the path string does not carry sixteen digits of nothing. */
const r = (n: number) => Math.round(n * 10) / 10
